/**
 * Collections API Service
 * Handles all collection and media-related API calls
 */

import apiClient from './index.js';

export class CollectionsApi {
    // Collections
    static async getCollections(token) {
        return apiClient.get('/create/v1/collections', token);
    }

    static async getCollectionDetails(token, collectionRelId) {
        // If numeric, treat as relId (owner's own collection).
        // If non-numeric UUID, treat as setId (linked collection).
        const isNumeric = /^\d+$/.test(collectionRelId);
        const param = isNumeric ? `relId=${collectionRelId}` : `setId=${collectionRelId}`;
        return apiClient.get(`/create/v1/collections/details?${param}`, token);
    }

    static async createCollection(token, data) {
        return apiClient.post('/create/v1/collections', token, data);
    }

    static async updateCollection(token, updateData) {
        return apiClient.patch('/create/v1/collections', token, updateData);
    }

    static async updateCollectionNotes(token, collectionId, notes) {
        return apiClient.patch('/create/v1/collections', token, { type: 'notes', collectionId, data: notes });
    }

    static async updateCollectionCopyright(token, collectionId, copyright) {
        return apiClient.patch('/create/v1/collections', token, { type: 'copyright', collectionId, data: copyright });
    }

    static async updateCollectionName(token, collectionId, name) {
        return apiClient.patch('/create/v1/collections', token, { type: 'collectionName', collectionId, data: name });
    }

    static async updateCollectionTier(token, collectionId, tier) {
        return apiClient.patch('/create/v1/collections', token, { type: 'tier', collectionId, data: tier });
    }

    static async updateCollectionStatus(token, collectionId, status) {
        return apiClient.patch('/create/v1/collections', token, { type: 'collectionStatus', collectionId, data: status });
    }

    static async updateCollectionActiveMonth(token, collectionId, activeMonth) {
        return apiClient.patch('/create/v1/collections', token, { type: 'activeMonth', collectionId, data: activeMonth });
    }

    static async updateCollectionFolderPath(token, collectionId, folderPath) {
        return apiClient.patch('/create/v1/collections', token, { type: 'folderPath', collectionId, data: folderPath });
    }

    static async batchUpdateCollections(token, updateType, items) {
        const data = (items || []).map(it => ({ collectionId: it.collectionId || it.photosetId, value: it.value }));
        return apiClient.patch('/create/v1/collections', token, { type: updateType, data });
    }

    static async deleteCollection(token, collectionId) {
        // Some environments drop DELETE bodies; pass id in query string
        const id = encodeURIComponent(collectionId);
        return apiClient.delete(`/create/v1/collections?id=${id}`, token);
    }

    static async linkCollection(token, collectionId, targetUserId, linkType) {
        return apiClient.post('/create/v1/collections/link', token, {
            collectionId,
            targetUserId,
            type: linkType || 'distribution'
        });
    }

    static async unlinkCollection(token, collectionId, targetUserId) {
        return apiClient.post('/create/v1/collections/unlink', token, {
            collectionId,
            targetUserId
        });
    }

    // Media
    static async getMedia(token, collectionId) {
        return apiClient.get(`/media/v1?collectionId=${collectionId}`, token);
    }

    static async addMedia(token, { collectionId, fileName, size, contentType, setId }) {
        // support legacy setId
        const cid = collectionId || setId;
        return apiClient.post('/media/v1', token, { collectionId: cid, fileName, size, contentType });
    }

    static async deleteMedia(token, { collectionId, mediaId, fileNames, setId, photoId }) {
        const cid = collectionId || setId;
        const mid = mediaId || photoId;
        const body = mid ? { id: mid } : { collectionId: cid, fileNames };
        return apiClient.delete('/media/v1', token, { body: JSON.stringify(body) });
    }

    static async updateMediaOrder(token, mediaOrder) {
        const data = (mediaOrder || []).map(p => ({ mediaId: p.mediaId || p.photoId, orderId: p.orderId }));
        return apiClient.patch('/create/v1/collections', token, { type: 'order', data });
    }

    static async updateMediaStatus(token, mediaId, mediaStatus) {
        return apiClient.patch('/media/v1', token, { id: mediaId, mediaStatus });
    }

    /**
     * SS-461: Re-invoke the S3 processor for a FAILED, UPLOADED, or stale
     * PROCESSING media item. Returns 200/409/410; throws on 4xx/5xx.
     */
    static async redriveMedia(token, mediaId) {
        return apiClient.post(`/media/v1/media/${mediaId}/redrive`, token, {});
    }

    /**
     * SS-470: Lightweight delta status poll. Returns only media items in the
     * collection whose `updatedAt` is greater than `since` (epoch seconds).
     * Used by the polling loop so unchanged thumbnails aren't re-signed on
     * every tick.
     */
    static async getMediaStatus(token, collectionId, since = 0) {
        const params = new URLSearchParams({ collectionId });
        if (since > 0) params.set('since', String(since));
        return apiClient.get(`/media/v1/media/status?${params.toString()}`, token);
    }

    static async downloadCollection(token, params) {
        return apiClient.post('/share/v1/download-collection', token, params);
    }
}

export default CollectionsApi;
